import { useRef, useState, useCallback } from "react";
import coinImg from "../assets/reward coin.png";
import { SoundManager } from "../SoundManager";
import { useI18n } from "../i18n";
import "./VictoryScreen.css";

interface VictoryScreenProps {
  currentLevel: number;
  totalLevels: number;
  coinsEarned: number;
  totalCoins: number;
  hasNextLevel: boolean;
  onNextLevel: () => void;
  onCollections: () => void;
}

interface FlyingCoin {
  id: number;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  delay: number;
}

export function VictoryScreen({
  currentLevel,
  totalLevels,
  coinsEarned,
  totalCoins,
  hasNextLevel,
  onNextLevel,
  onCollections,
}: VictoryScreenProps) {
  const { t, formatString } = useI18n();
  const [displayedCoins, setDisplayedCoins] = useState(
    totalCoins - coinsEarned,
  );
  const [flyingCoins, setFlyingCoins] = useState<FlyingCoin[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const coinSourceRef = useRef<HTMLDivElement>(null);
  const coinTargetRef = useRef<HTMLDivElement>(null);

  const startCoinAnimation = useCallback(() => {
    if (coinsEarned <= 0 || isAnimating) {
      onNextLevel();
      return;
    }

    setIsAnimating(true);

    const sourceRect = coinSourceRef.current?.getBoundingClientRect();
    const targetRect = coinTargetRef.current?.getBoundingClientRect();

    if (!sourceRect || !targetRect) {
      onNextLevel();
      return;
    }

    // Create flying coins - always 5 for satisfying animation
    const numCoins = 5;
    const coins: FlyingCoin[] = [];

    for (let i = 0; i < numCoins; i++) {
      coins.push({
        id: i,
        x:
          sourceRect.left +
          sourceRect.width / 2 -
          15 +
          (Math.random() - 0.5) * 40,
        y:
          sourceRect.top +
          sourceRect.height / 2 -
          15 +
          (Math.random() - 0.5) * 40,
        targetX: targetRect.left + targetRect.width / 2 - 15,
        targetY: targetRect.top + targetRect.height / 2 - 15,
        delay: i * 150,
      });
    }

    setFlyingCoins(coins);

    // Increment counter and play sound as coins arrive
    const coinsPerIncrement = coinsEarned / numCoins;
    const soundManager = SoundManager.getInstance();
    coins.forEach((coin) => {
      setTimeout(() => {
        soundManager.play("coin");
        setDisplayedCoins((prev) =>
          Math.min(totalCoins, Math.round(prev + coinsPerIncrement)),
        );
      }, coin.delay + 400); // 400ms is the flight duration
    });

    // Call onNextLevel after animation completes
    const lastCoinDelay = coins[coins.length - 1]?.delay || 0;
    setTimeout(() => {
      onNextLevel();
    }, lastCoinDelay + 600); // Wait for last coin + a bit extra
  }, [coinsEarned, totalCoins, isAnimating, onNextLevel]);

  const handleNextClick = () => {
    startCoinAnimation();
  };

  return (
    <div className="victory-overlay">
      {/* Coin counter in top-right */}
      <div className="victory-coin-counter" ref={coinTargetRef}>
        <img src={coinImg} alt="Coins" />
        <span>{displayedCoins}</span>
      </div>

      {/* Flying coins */}
      {flyingCoins.map((coin) => (
        <img
          key={coin.id}
          src={coinImg}
          className="flying-coin"
          style={
            {
              left: coin.x,
              top: coin.y,
              "--target-x": `${coin.targetX - coin.x}px`,
              "--target-y": `${coin.targetY - coin.y}px`,
              animationDelay: `${coin.delay}ms`,
            } as React.CSSProperties
          }
          alt=""
        />
      ))}

      <div className="victory-panel">
        <div className="victory-content">
          {coinsEarned > 0 && (
            <div className="coins-earned" ref={coinSourceRef}>
              <img src={coinImg} alt="Coins" />
              <span>+{coinsEarned}</span>
            </div>
          )}

          <div className="level-info">
            {hasNextLevel
              ? formatString(t.levelOf, {
                  current: currentLevel,
                  total: totalLevels,
                })
              : t.collectionCompleted}
          </div>

          <div className="victory-buttons">
            {hasNextLevel ? (
              <button
                className="next-btn"
                onClick={handleNextClick}
                disabled={isAnimating}
              >
                {t.nextPuzzle}
              </button>
            ) : (
              <button
                className="collections-btn"
                onClick={onCollections}
                disabled={isAnimating}
              >
                {t.collections}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
